/**
 * <product-card-gallery> wraps a single native horizontal scroll track that
 * holds every product image. The same <img> elements serve both interactions
 * so nothing is ever fetched twice:
 *
 *  - Touch / no-hover devices: the track scrolls natively (CSS scroll-snap),
 *    so swiping needs no JS at all, and the browser's own scroll-vs-tap
 *    disambiguation means a swipe never accidentally opens the product page.
 *  - Mouse / hover-capable devices: overflow is locked (see the CSS in
 *    gc-product-card.liquid), so the track can only move programmatically —
 *    this component nudges it to slide 2 on mouseenter and back on
 *    mouseleave, giving the classic hover-to-second-image behaviour.
 *
 * Pagination dots (rendered in Liquid, one per image) just mirror whichever
 * slide is currently in view, via a single IntersectionObserver.
 */
class ProductCardGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));

    if (!this.track || this.slides.length < 2) return;

    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    // The track starts scrolled to slide 0, so mark its dot active before the
    // observer's first (async) callback lands — that first callback can fire
    // during initial layout, before the browser has settled which slides
    // actually intersect, so it isn't trustworthy on its own (see below).
    this.setActiveDot(0);

    this.observer = new IntersectionObserver(this.handleIntersect, {
      root: this.track,
      threshold: 0.6,
    });
    this.slides.forEach((slide) => this.observer.observe(slide));

    this.addEventListener('mouseenter', this.handleEnter);
    this.addEventListener('mouseleave', this.handleLeave);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('mouseenter', this.handleEnter);
    this.removeEventListener('mouseleave', this.handleLeave);
  }

  handleIntersect = (entries) => {
    // During the very first callback (fired while initial layout is still
    // settling) more than one slide can report isIntersecting simultaneously;
    // applying each in array order made whichever slide came last "win" even
    // when it wasn't the most visible one. Instead, only the most-visible
    // slide in this batch gets to claim the active dot.
    let bestIndex = -1;
    let bestRatio = 0;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.slides.indexOf(entry.target);
      if (index === -1) continue;
      if (entry.intersectionRatio > bestRatio) {
        bestRatio = entry.intersectionRatio;
        bestIndex = index;
      }
    }
    if (bestIndex > -1) this.setActiveDot(bestIndex);
  };

  handleEnter = () => {
    if (!this.hoverQuery.matches || !this.track) return;
    this.track.scrollLeft = this.track.clientWidth;
  };

  handleLeave = () => {
    if (!this.hoverQuery.matches || !this.track) return;
    this.track.scrollLeft = 0;
  };

  setActiveDot(index) {
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }
}

if (!customElements.get('product-card-gallery')) {
  customElements.define('product-card-gallery', ProductCardGallery);
}
